import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useBarcodeScan } from '../../hooks/useBarcodeScan';
import { storageApi, PickList } from '../../api/storage.api';
import {
  MapPin, ScanLine, Check, ChevronRight, AlertTriangle,
  ArrowLeft, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

type ScanStatus = 'waiting' | 'correct' | 'incorrect';

export default function PickingGuidedPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [pickList, setPickList] = useState<PickList | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scanStatus, setScanStatus] = useState<ScanStatus>('waiting');
  const [pickedQuantity, setPickedQuantity] = useState<number>(0);
  const [picking, setPicking] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (id && tenant?.id) loadPickList();
  }, [id, tenant?.id]);

  const loadPickList = async () => {
    try {
      setLoading(true);
      const data = await storageApi.getPickList(id!);
      setPickList(data);

      // Trouver la première ligne non pickée
      const firstUnpicked = data.lines?.findIndex(l => !l.picked) ?? 0;
      setCurrentIndex(Math.max(0, firstUnpicked));

      // Pré-remplir la quantité de la ligne courante
      const currentLine = data.lines?.[Math.max(0, firstUnpicked)];
      if (currentLine) setPickedQuantity(currentLine.quantity);
    } catch {
      toast.error('Erreur lors du chargement du pick list');
    } finally {
      setLoading(false);
    }
  };

  const lines = pickList?.lines || [];
  const currentLine = lines[currentIndex];
  const allPicked = lines.length > 0 && lines.every(l => l.picked);
  const pickedCount = lines.filter(l => l.picked).length;

  // Quand on change de ligne, pré-remplir la quantité
  useEffect(() => {
    if (currentLine) {
      setPickedQuantity(currentLine.quantity);
      setScanStatus('waiting');
    }
  }, [currentIndex]);

  const handleScan = useCallback((scanned: string) => {
    if (!currentLine || currentLine.picked) return;

    if (scanned.startsWith('LOC:')) {
      // Vérifier si le QR correspond à l'emplacement attendu
      if (currentLine.location?.id) {
        // On a un emplacement attendu — comparer les QR codes
        storageApi.getLocationByQR(scanned).then(loc => {
          if (loc.id === currentLine.location?.id) {
            setScanStatus('correct');
            toast.success(`Emplacement correct : ${loc.label}`);
            if (navigator.vibrate) navigator.vibrate(50);
          } else {
            setScanStatus('incorrect');
            toast.error(`Mauvais emplacement ! Attendu : ${currentLine.location_label}`);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
          }
        }).catch(() => {
          setScanStatus('incorrect');
          toast.error('Emplacement non reconnu');
        });
      } else {
        // Pas d'emplacement attendu (stock non rangé) → accepter tout
        setScanStatus('correct');
        toast.success('Scan accepté');
      }
    }
  }, [currentLine]);

  useBarcodeScan({ onScan: handleScan, enabled: !allPicked && !picking });

  const handlePickLine = async () => {
    if (!currentLine || !pickList) return;

    setPicking(true);
    try {
      // Utiliser le QR de l'emplacement ou un placeholder
      const scannedQR = currentLine.location
        ? (currentLine.location as any).qr_code || `LOC:${currentLine.location_id}`
        : 'LOC:unassigned';

      await storageApi.pickLine(pickList.id, currentLine.id, {
        scannedQR,
        pickedQuantity,
      });

      // Recharger le pick list
      await loadPickList();

      // Passer à la ligne suivante
      const nextUnpicked = lines.findIndex((l, i) => i > currentIndex && !l.picked);
      if (nextUnpicked >= 0) {
        setCurrentIndex(nextUnpicked);
      }

      toast.success('Ligne pickée');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors du picking');
    } finally {
      setPicking(false);
    }
  };

  const handleComplete = async () => {
    if (!pickList) return;
    setCompleting(true);
    try {
      await storageApi.completePickList(pickList.id);
      toast.success('Pick list terminé !');
      navigate('/storage/picking');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400">Chargement...</div>;
  }

  if (!pickList) {
    return <div className="p-6 text-center text-red-500">Pick list non trouvé</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/storage/picking')} className="p-1 hover:bg-gray-100 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="font-mono font-bold">{pickList.pick_number}</p>
            <p className="text-xs text-gray-500">
              {pickList.type === 'ORDER' ? `Commande ${pickList.order?.order_number || ''}` : 'Réappro rayon'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold">{pickedCount}/{lines.length}</p>
          <p className="text-xs text-gray-400">lignes</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 shrink-0">
        <div
          className="h-full bg-orange-500 transition-all duration-300"
          style={{ width: `${lines.length > 0 ? (pickedCount / lines.length) * 100 : 0}%` }}
        />
      </div>

      {/* Contenu principal */}
      <div className="flex-1 overflow-y-auto p-4">
        {allPicked ? (
          /* Tout est pické */
          <div className="text-center py-12">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-700">Picking terminé !</h2>
            <p className="text-gray-500 mt-2">Toutes les lignes ont été pickées</p>
            <button
              onClick={handleComplete}
              disabled={completing}
              className="mt-6 px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 min-h-[48px]"
            >
              {completing ? 'Finalisation...' : 'Valider et terminer'}
            </button>
          </div>
        ) : currentLine ? (
          /* Ligne courante */
          <div>
            {/* Emplacement — gros */}
            <div className={`mb-4 p-6 rounded-2xl text-center ${
              currentLine.location_label === 'Non rangé'
                ? 'bg-yellow-50 border-2 border-yellow-300'
                : 'bg-purple-50 border-2 border-purple-300'
            }`}>
              <MapPin className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-4xl font-bold font-mono tracking-wider">
                {currentLine.location_label || 'Non rangé'}
              </p>
              {currentLine.location && (
                <p className="text-sm text-gray-500 mt-1">
                  Allée {currentLine.location.aisle} / Col {currentLine.location.column} / Étag {currentLine.location.shelf}
                </p>
              )}
            </div>

            {/* Produit */}
            <div className="mb-4 p-4 bg-white rounded-xl border">
              <p className="text-sm text-gray-500">Produit à prendre</p>
              <p className="text-xl font-bold">{currentLine.product_name}</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {currentLine.quantity} {/* TODO: unit */}
              </p>
            </div>

            {/* Zone de scan */}
            <div className={`mb-4 p-4 rounded-xl border-2 text-center transition-colors ${
              scanStatus === 'correct' ? 'bg-green-50 border-green-400' :
              scanStatus === 'incorrect' ? 'bg-red-50 border-red-400' :
              'bg-gray-50 border-dashed border-gray-300'
            }`}>
              {scanStatus === 'waiting' && (
                <>
                  <ScanLine className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Scannez le QR de l'emplacement pour confirmer</p>
                </>
              )}
              {scanStatus === 'correct' && (
                <>
                  <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-green-700 font-medium">Emplacement confirmé</p>
                </>
              )}
              {scanStatus === 'incorrect' && (
                <>
                  <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-red-700 font-medium">
                    Mauvais emplacement ! Attendu : {currentLine.location_label}
                  </p>
                  <button
                    onClick={() => setScanStatus('waiting')}
                    className="mt-2 text-xs text-red-500 underline"
                  >
                    Réessayer
                  </button>
                </>
              )}
            </div>

            {/* Quantité ajustable */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1">Quantité prise</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPickedQuantity(q => Math.max(0, q - 1))}
                  className="w-12 h-12 bg-gray-100 rounded-lg text-lg font-bold hover:bg-gray-200"
                >-</button>
                <input
                  type="number"
                  step="0.001"
                  value={pickedQuantity}
                  onChange={e => setPickedQuantity(parseFloat(e.target.value) || 0)}
                  className="flex-1 px-4 py-3 text-center text-xl font-bold border rounded-lg"
                />
                <button
                  onClick={() => setPickedQuantity(q => q + 1)}
                  className="w-12 h-12 bg-gray-100 rounded-lg text-lg font-bold hover:bg-gray-200"
                >+</button>
              </div>
            </div>

            {/* Bouton picker */}
            <button
              onClick={handlePickLine}
              disabled={picking || pickedQuantity <= 0}
              className="w-full py-4 bg-orange-600 text-white rounded-xl font-bold text-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[56px]"
            >
              {picking ? (
                'Enregistrement...'
              ) : (
                <>
                  <Check className="w-6 h-6" />
                  Confirmer ({pickedQuantity})
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Mini liste des lignes restantes */}
            <div className="mt-6">
              <p className="text-xs font-medium text-gray-500 mb-2">Lignes restantes</p>
              <div className="space-y-1">
                {lines.filter(l => !l.picked).map((l) => (
                  <div
                    key={l.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      l.id === currentLine.id ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'
                    }`}
                  >
                    <span className="font-mono text-xs text-gray-400 w-8">{l.location_label || '?'}</span>
                    <span className="truncate flex-1">{l.product_name}</span>
                    <span className="font-medium shrink-0">{l.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
